import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

const listeners = new Set<() => void>();
let listening = false;

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!listening) {
    window.addEventListener('popstate', emit);
    listening = true;
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && listening) {
      window.removeEventListener('popstate', emit);
      listening = false;
    }
  };
}

function snapshot(): string {
  return window.location.pathname;
}

export function routeLocationSnapshot(location: Pick<Location, 'pathname' | 'search'> = window.location): string {
  return `${location.pathname}${location.search}`;
}

function routeSnapshot(): string {
  return routeLocationSnapshot();
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, snapshot, () => '/');
}

export function useRouteKey(): string {
  return useSyncExternalStore(subscribe, routeSnapshot, () => '/');
}

export function navigate(path: string, replace = false): void {
  if (replace) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  emit();
}

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };

export function Link({ to, onClick, ...props }: LinkProps) {
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={follow} {...props} />;
}
